import { useCallback, useEffect, useRef, useState } from 'react';
import { ADMISSION_STATUSES, pillClassFor } from '../../constants/admissionStatus';
import { useUpdateAdmissionStatus } from '../../hooks/useUpdateAdmissionStatus';
import '../../styles/CaseManagement.css';

function AdmissionStatusPicker({ caseId, currentStatus, archived }) {
    const [isOpen, setIsOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);
    const containerRef = useRef(null);
    const pillRef = useRef(null);

    const { change, isPending } = useUpdateAdmissionStatus({
        caseId,
        currentStatus: currentStatus || 'In progress',
        onOptimisticChange: setPendingStatus,
    });

    // Clear optimistic value once Firestore snapshot catches up to it.
    useEffect(() => {
        if (pendingStatus !== null && pendingStatus === currentStatus) {
            setPendingStatus(null);
        }
    }, [pendingStatus, currentStatus]);

    const displayStatus = pendingStatus ?? currentStatus ?? 'In progress';
    const pillClass = pillClassFor(displayStatus);

    const close = useCallback(() => setIsOpen(false), []);

    // Outside click + Escape
    useEffect(() => {
        if (!isOpen) return undefined;
        function onMouseDown(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                close();
            }
        }
        function onKeyDown(e) {
            if (e.key === 'Escape') {
                close();
                if (pillRef.current) pillRef.current.focus();
            }
        }
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen, close]);

    const handlePillClick = () => {
        if (archived) return;
        setIsOpen((v) => !v);
    };

    const handleSelect = (status) => {
        close();
        change(status);
    };

    return (
        <span className="admission-picker" ref={containerRef}>
            <button
                ref={pillRef}
                type="button"
                className={`admission-pill ${pillClass}`}
                onClick={handlePillClick}
                disabled={archived}
                aria-haspopup={archived ? undefined : 'listbox'}
                aria-expanded={archived ? undefined : isOpen}
                aria-label={`Admission status: ${displayStatus}. Click to change.`}
            >
                <span className="admission-pill-label">{displayStatus}</span>
                {!archived && <span className="admission-pill-chevron" aria-hidden="true">▾</span>}
                {isPending && <span className="admission-picker-spinner" aria-hidden="true" />}
            </button>
            {isOpen && (
                <ul role="listbox" className="admission-picker-listbox">
                    {ADMISSION_STATUSES.map((status) => {
                        const selected = status === displayStatus;
                        return (
                            <li
                                key={status}
                                role="option"
                                aria-selected={selected}
                                tabIndex={0}
                                className={`admission-picker-option${selected ? ' selected' : ''}`}
                                onClick={() => handleSelect(status)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleSelect(status);
                                    }
                                }}
                            >
                                {status}
                            </li>
                        );
                    })}
                </ul>
            )}
        </span>
    );
}

export default AdmissionStatusPicker;
